import { useMedplum } from '@medplum/react';
import { ProviderRole } from '@bariendo/lib/constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatHumanName, isResource, MedplumClient } from '@medplum/core';
import { Practitioner, PractitionerRole, Resource } from '@medplum/fhirtypes';

export interface PractitionerOption {
  value: string;
  label: string;
}

export async function searchPractitionersByRoles(
  medplum: MedplumClient,
  roles?: ProviderRole[]
): Promise<{ practitioners: Practitioner[]; practitionerRoles: PractitionerRole[] }> {
  const searchParams: Record<string, string | number> = {
    _count: 1000,
    _include: 'PractitionerRole:practitioner',
  };

  if (roles?.length) {
    searchParams.role = roles.join(',');
  }

  const resources = (await medplum.searchResources('PractitionerRole', searchParams)) as Resource[];

  return {
    practitioners: resources.filter((resource) => isResource(resource, 'Practitioner')) as Practitioner[],
    practitionerRoles: resources.filter((resource) => isResource(resource, 'PractitionerRole')) as PractitionerRole[],
  };
}

export function buildPractitionerOptions(practitioners: Practitioner[]): PractitionerOption[] {
  return [
    ...new Map(
      practitioners.map((practitioner) => [
        practitioner.id!,
        {
          value: practitioner.id!,
          label: formatHumanName(practitioner.name?.[0]) || practitioner.id!,
        },
      ])
    ).values(),
  ].sort((a, b) => a.label.localeCompare(b.label));
}

export function usePractitionersByRoles(roles?: ProviderRole[]) {
  const medplum = useMedplum();
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [practitionerRoles, setPractitionerRoles] = useState<PractitionerRole[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);

    try {
      const result = await searchPractitionersByRoles(medplum, roles);
      setPractitioners(result.practitioners);
      setPractitionerRoles(result.practitionerRoles);
    } catch {
      setPractitioners([]);
      setPractitionerRoles([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, roles]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const practitionerOptions = useMemo(() => buildPractitionerOptions(practitioners), [practitioners]);
  const practitionersById = useMemo(() => new Map(practitioners.map((p) => [p.id!, p])), [practitioners]);

  return {
    practitioners,
    practitionerRoles,
    practitionerOptions,
    practitionersById,
    loading,
    reload,
  };
}
